import { computed, ref, watchEffect } from 'vue'
import { defineStore } from 'pinia';
import { deepMerge } from '@/helpers/deepMerge'

const shapeBaseSettings = {
  polyline: {
    strokeColor: 'hsl(0, 100%, 50%)',
    strokeOpacity: 100,
    strokeWidth: 3
  },
  polygon: {
    fill: 'hsl(0, 0%, 100%)',
    fillOpacity: 25,
    strokeColor: 'hsl(0, 100%, 50%)',
    strokeOpacity: 100,
    strokeWidth: 3
  },
  circle: {
    fill: 'hsl(0, 0%, 100%)',
    fillOpacity: 25,
    strokeColor: 'hsl(0, 100%, 50%)',
    strokeOpacity: 100,
    strokeWidth: 3,
    radius: 0.0
  }
}

export let useMapDrawingStore = defineStore('mapDrawingStore', () => {
  const shapes = ref([]);
  const mode = ref('viewing');
  const activeShapeId = ref(null);
  const selectedDrawingTool = ref(null);
  const showDistanceMarkers = ref(true);
  const showHeadingMarkers = ref(false);

  const activeShape = computed(() => {
    if (!activeShapeId.value || shapes.value.length === 0) return null;

    return shapes.value.find(shape => shape.id === activeShapeId.value);
  })

  const createShape = (shapeType) => {
    const id = `${shapeType}-${Date.now()}-${shapes.value.length}`;
    const shapeTypeCount = shapes.value.reduce((acc, shape) => {
      return shapeType === shape.type ? acc + 1 : acc;
    }, 1);
    const settings = shapeBaseSettings[shapeType];

    return {
      id: id,
      type: shapeType,
      name: `${shapeType.charAt(0).toUpperCase() + shapeType.slice(1)} ${shapeTypeCount}`,
      points: [],
      latLons: [],
      closed: shapeType === 'circle',
      settings: {...settings},
    }
  }

  const addShape = (shapeType, config = {}) => {
    const baseShape = createShape(shapeType);
    const shape = deepMerge({}, baseShape, config);

    activeShapeId.value = shape.id;
    shapes.value.push(shape);

    return shape
  }

  const getShape = (shapeId) => {
    return shapeId ? shapes.value.find((shape) => shape.id === shapeId) : activeShape.value;
  }

  const deleteShape = (shapeId = activeShape.value.id) => {
    if (!shapeId) return;

    const shape = getShape(shapeId);

    if (shape.shared) {
      const paramsStartIndex = window.location.href.indexOf('?');
      const urlWithNoQueryString = window.location.href.substring(0, paramsStartIndex);

      history.pushState(null, '', urlWithNoQueryString);
    }

    shapes.value = shapes.value.filter(shape => shape.id !== shapeId);
  }

  const deleteAllShapes = () => {
    shapes.value = [];
  }

  const setDrawingMode = (modeValue) => {
    mode.value = modeValue;

    document.documentElement.dataset.drawingMode = modeValue;
    shapeCleanup();
  }

  const setSelectedDrawingTool = (tool) => {
    selectedDrawingTool.value = tool;
    shapeCleanup();
  }

  const setActiveShapeId = (id) => {
    activeShapeId.value = id;
  }

  const setShapeName = (shapeId, name) => {
    const shape = getShape(shapeId);

    if (shape) {
      shape.name = name;
    }
  }

  const setMetadata = (shapeId, key, value) => {
    const shape = getShape(shapeId);

    if (shape) {
      shape.meta = shape.meta || {};
      shape.meta[key] = value;
    }
  }

  const setShapeStrokeColor = (color, shapeId) => {
    const shape = getShape(shapeId);

    if (shape) {
      shape.settings.strokeColor = color;
    }
  }

  const setShapeFillColor = (color, shapeId) => {
    const shape = getShape(shapeId);

    if (shape) {
      shape.settings.fill = color;
    }
  }

  const setShapeFillOpacity = (opacity, shapeId) => {
    const shape = getShape(shapeId)

    if (shape) {
      shape.settings.fillOpacity = opacity
    }
  }

  const setShapeStrokeOpacity = (opacity, shapeId) => {
    const shape = getShape(shapeId)

    if (shape) {
      shape.settings.strokeOpacity = opacity
    }
  }

  const setShapeStrokeWidth = (width, shapeId) => {
    const shape = getShape(shapeId);

    if (shape) {
      shape.settings.strokeWidth = width
    }
  }

  const setCircleRadius = (radius, shapeId) => {
    const shape = getShape(shapeId);

    if (shape && shape.type === 'circle') {
      shape.settings.radius = radius;
    }
  }

  const deactivateDrawingMode = () => {
    mode.value = 'viewing';
    selectedDrawingTool.value = null;
    activeShapeId.value = null;

    shapeCleanup();
  }

  const shapeCleanup = () => {
    const nonActiveShapes = shapes.value.filter(shape => shape.id !== activeShapeId.value);

    nonActiveShapes.forEach(shape => {
      if (shape.points.length === 1 || shape.latLons.length === 1) {
        deleteShape(shape.id);
      }

      if (shape.type === 'polygon' && !shape.closed && shape.points.length >= 3) {
        shape.closed = true;
      } 
    });
  }

  watchEffect(() => {
    if (mode.value === 'viewing') {
      activeShapeId.value = null;
      selectedDrawingTool.value = null;
      activeShapeId.value = null;
    }
  })

  return {
    activeShape,
    mode,
    selectedDrawingTool,
    shapes,
    showDistanceMarkers,
    showHeadingMarkers,
    addShape,
    deactivateDrawingMode,
    deleteAllShapes,
    deleteShape,
    setActiveShapeId,
    setCircleRadius,
    setDrawingMode,
    setMetadata,
    setSelectedDrawingTool,
    setShapeStrokeColor,
    setShapeFillColor,
    setShapeFillOpacity,
    setShapeStrokeOpacity,
    setShapeStrokeWidth,
    setShapeName
  }
});
